/**
 * P103 build mode (master plan, file-74e469bb.pdf): "Drag-to-paint, a cost
 * preview, invalid tiles in red, confirm or cancel, and undo of the last
 * action" - done when "a Playwright one-handed drag test passes, and cancel
 * leaves cash unchanged". Strokes are STAGED locally: painting charges
 * nothing; Confirm sends one fence.place per stroke (4A edgeM x grade rate,
 * grade picked up front; 5A gated edge = the fence length, no surcharge);
 * Cancel discards; Undo drops the last staged stroke. Dragging over an
 * already-built edge paints it red (invalid) and stages nothing. Gating an
 * EXISTING built edge converts it immediately - 5A free conversion, no
 * currency involved. Fence REMOVAL (demolition) is deliberately not exposed
 * here: the plan's 40% salvage is scoped to "conversions and sales" and the
 * removal refund rule is an unresolved owner decision (2026-10-23).
 * Viewport: cell-size floor + native scroll pan + zoom so edges stay
 * finger-true on large parcels.
 */
import { useRef, useState } from 'preact/hooks';
import { useStore } from '../store';
import { fmtMoney, type UiEnclosureType, type UiFenceLayout } from '../bridge';
import { findPens } from '../../sim/spatial/fences';
import { pickEdge, viewBoxPoint } from '../fence-hit';

const GRADES = [
  { grade: 1, name: 'Basic', perMCents: 800, stroke: '#A8894E', width: 1.4 },
  { grade: 2, name: 'Heavy', perMCents: 2000, stroke: '#4A5A2E', width: 2.0 },
  { grade: 3, name: 'Predator-proof', perMCents: 4500, stroke: '#3A2E22', width: 2.8 },
] as const;

type Tool = 'fence' | 'gate' | 'pan' | 'remove' | 'resize';
interface Stroke { edge: string; grade: 1 | 2 | 3; gate: boolean }

function edgeLine(key: string, edgeM: number): { x1: number; y1: number; x2: number; y2: number } | null {
  const m = /^(\d+),(\d+),([NW])$/.exec(key);
  if (!m) return null;
  const x = Number(m[1]); const y = Number(m[2]);
  return m[3] === 'N'
    ? { x1: x * edgeM, y1: y * edgeM, x2: (x + 1) * edgeM, y2: y * edgeM }
    : { x1: x * edgeM, y1: y * edgeM, x2: x * edgeM, y2: (y + 1) * edgeM };
}

const MIN_CELL_PX = 16;
const MAX_CELL_PX = 56;

export function FenceDrawing({ parcelId, layout }: { parcelId: string; layout: UiFenceLayout }) {
  const { bridge, sim } = useStore();
  const [claimType, setClaimType] = useState('paddock');
  const [tool, setTool] = useState<Tool>('fence');
  const [grade, setGrade] = useState<1 | 2 | 3>(2);
  const [pending, setPending] = useState<Stroke[]>([]);
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null); // built edge awaiting demolition confirm
  // P106 resize: a claimed pen picked by tapping it, then staged add/remove
  // edges applied as ONE atomic enclosure.resize (interim: instant - downtime pending).
  const [resizeEnc, setResizeEnc] = useState<{ id: string; anchor: number; cells: number[] } | null>(null);
  const [resizeAdd, setResizeAdd] = useState<Stroke[]>([]);
  const [resizeRemove, setResizeRemove] = useState<string[]>([]);
  const [invalidEdge, setInvalidEdge] = useState<string | null>(null);
  const [cellPx, setCellPx] = useState(30);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const lastEdge = useRef<string | null>(null);
  const dragDir = useRef<'N' | 'W' | null>(null); // strict per-drag orientation lock (set by first unambiguous paint or by travel axis)
  const dragStart = useRef<{ px: number; py: number } | null>(null); // grid-meter point of pointer-down (vertex starts defer the lock to travel)
  const { cols, rows, edgeM } = layout;
  const W = cols * edgeM;
  const H = rows * edgeM;
  const segGrade = new Map<string, number>();
  layout.segments.forEach((key, i) => { segGrade.set(key, layout.grades?.[i] ?? 0); });
  const gateSet = new Set(layout.gates);
  const segSet = new Set(layout.segments);
  const stagedSet = new Set(pending.map((p) => p.edge));
  // P104: closed pens not yet claimed by an enclosure - claimable from here.
  const claimedSet = new Set(layout.claimed ?? []);
  const unclaimedPens = pending.length === 0
    ? findPens({ cols, rows, edgeM }, { segments: new Set(layout.segments), gates: new Set(layout.gates) }, () => true)
        .filter((pen) => !pen.cells.some((c) => claimedSet.has(c)))
    : [];
  const g = GRADES[grade - 1];
  const previewCents = pending.reduce((a, p) => a + edgeM * GRADES[p.grade - 1].perMCents, 0);
  // Claimed pens on this parcel (P106 resize selection).
  const claimedHere = sim.enclosures
    .filter((enc) => enc.parcelId === parcelId && enc.id in sim.penCells && sim.penCells[enc.id].length > 0)
    .map((enc) => ({ enc, cells: sim.penCells[enc.id] }));
  const resizeEncInfo = resizeEnc ? claimedHere.find((c) => c.enc.id === resizeEnc.id) : undefined;
  const cellFromEvent = (e: PointerEvent): number | null => {
    const pt = pointOf(e);
    if (pt === null) return null;
    const cx = Math.floor(pt.px / edgeM); const cy = Math.floor(pt.py / edgeM);
    if (cx < 0 || cy < 0 || cx >= cols || cy >= rows) return null;
    return cy * cols + cx;
  };

  /** Nearest canonical edge to a client point, within 45% of a cell. */
  const edgeFromEvent = (e: PointerEvent): string | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const { px, py } = viewBoxPoint(e.clientX, e.clientY, svg.getBoundingClientRect(), W, H);
    const hit = pickEdge(px, py, cols, rows, edgeM, dragDir.current);
    if (hit !== null) dragDir.current = hit.dir;
    return hit?.edge ?? null;
  };

  const pointOf = (e: PointerEvent): { px: number; py: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    return viewBoxPoint(e.clientX, e.clientY, svg.getBoundingClientRect(), W, H);
  };

  const paintAt = (e: PointerEvent): void => {
    // Travel-axis lock: a start near a vertex (both orientations eligible)
    // does not paint or lock until the finger has moved ~0.3 cell; the
    // dominant travel axis then picks the orientation, and the start point's
    // edge is painted in that orientation too (no skipped first edge).
    const pt = pointOf(e);
    if (pt === null) return;
    if (dragDir.current === null && dragStart.current !== null) {
      const dx = pt.px - dragStart.current.px; const dy = pt.py - dragStart.current.py;
      if (Math.hypot(dx, dy) >= edgeM * 0.3) {
        dragDir.current = Math.abs(dx) >= Math.abs(dy) ? 'N' : 'W';
        const startHit = pickEdge(dragStart.current.px, dragStart.current.py, cols, rows, edgeM, dragDir.current);
        if (startHit !== null && !stagedSet.has(startHit.edge) && !segSet.has(startHit.edge)) {
          stagedSet.add(startHit.edge);
          setPending((p) => [...p, { edge: startHit.edge, grade, gate: tool === 'gate' }]);
        }
      }
    }
    const key = edgeFromEvent(e);
    if (key === lastEdge.current) return;
    lastEdge.current = key;
    if (!key || stagedSet.has(key)) return;
    if (segSet.has(key)) {
      // Built fence: not paintable (invalid - red). Gate conversion is a free
      // tap; the Remove tool demolishes with the owner-decided 40% salvage.
      if (tool === 'gate' && !gateSet.has(key)) {
        bridge.send('fence.place', { parcelId, edge: key, grade, gate: true });
        return;
      }
      if (tool === 'remove') {
        setPendingRemoval(key); // demolition is consequential: confirm card, never an unannounced tap
        return;
      }
      setInvalidEdge(key);
      return;
    }
    setInvalidEdge(null);
    setPending((p) => [...p, { edge: key, grade, gate: tool === 'gate' }]);
  };

  const paintResizeAt = (e: PointerEvent): void => {
    const key = edgeFromEvent(e);
    if (key === lastEdge.current) return;
    lastEdge.current = key;
    if (!key) return;
    if (segSet.has(key)) {
      if (!resizeEnc?.cells.length) return;
      setResizeRemove((r) => (r.includes(key) ? r.filter((k) => k !== key) : [...r, key]));
      return;
    }
    setResizeAdd((a) => (a.some((x) => x.edge === key) || resizeRemove.includes(key) ? a : [...a, { edge: key, grade, gate: false }]));
  };
  // Route drag painting in resize mode.
  const paintAtRouted = (e: PointerEvent): void => {
    if (tool === 'resize') { paintResizeAt(e); return; }
    paintAt(e);
  };

  const onPointerDown = (e: PointerEvent): void => {
    e.preventDefault();
    dragging.current = true;
    lastEdge.current = null;
    dragStart.current = pointOf(e);
    try { (e.target as Element).releasePointerCapture(e.pointerId); } catch { /* pointer no longer captured */ }
    if (tool === 'resize') {
      const cell = cellFromEvent(e);
      const nearEdge = pickEdge(dragStart.current?.px ?? -1, dragStart.current?.py ?? -1, cols, rows, edgeM, null);
      if (resizeEnc === null) {
        if (cell === null) return;
        const hit = claimedHere.find((c) => c.cells.includes(cell));
        if (hit === undefined) return;
        setResizeEnc({ id: hit.enc.id, anchor: cell, cells: hit.cells });
        return; // first tap only selects
      }
      if (nearEdge === null && cell !== null) {
        // Mid-cell tap: pick the keep-side anchor (must stay inside the pen) or switch pens.
        if (resizeEnc.cells.includes(cell)) { setResizeEnc({ ...resizeEnc, anchor: cell }); return; }
        const other = claimedHere.find((c) => c.enc.id !== resizeEnc.id && c.cells.includes(cell));
        if (other !== undefined) { setResizeEnc({ id: other.enc.id, anchor: cell, cells: other.cells }); setResizeAdd([]); setResizeRemove([]); return; }
      }
      paintAtRouted(e);
      return;
    }
    paintAt(e); // unambiguous mid-edge touch paints + locks immediately; a vertex start waits for travel
  };
  const onPointerMove = (e: PointerEvent): void => { if (dragging.current) paintAtRouted(e); };
  const endDrag = (): void => { dragging.current = false; lastEdge.current = null; dragDir.current = null; dragStart.current = null; setInvalidEdge(null); };

  const confirm = (): void => {
    for (const p of pending) bridge.send('fence.place', { parcelId, edge: p.edge, grade: p.grade, gate: p.gate });
    setPending([]);
  };

  // Demolition consequences (shown BEFORE the player commits): 40% salvage
  // (owner decision 2026-10-23, B) + any claimed enclosure whose loop this
  // edge breaks dissolves and its animals escape (P104).
  const removalInfo = (() => {
    if (pendingRemoval === null) return null;
    const grade = segGrade.get(pendingRemoval) ?? 0;
    const refundCents = grade >= 1 ? Math.round(edgeM * GRADES[grade - 1].perMCents * 0.4) : 0;
    const after = layout.segments.filter((k) => k !== pendingRemoval);
    const pensAfter = findPens({ cols, rows, edgeM }, { segments: new Set(after), gates: new Set(layout.gates.filter((g) => g !== pendingRemoval)) }, () => true)
      .map((pen) => new Set(pen.cells));
    const dissolving = sim.enclosures.filter((enc) => {
      if (enc.parcelId !== parcelId) return false;
      const cells = sim.penCells[enc.id] as number[] | undefined;
      if (!cells || cells.length === 0) return false;
      return !pensAfter.some((set) => set.size === cells.length && cells.every((c) => set.has(c)));
    });
    const escaped = dissolving.reduce((a, enc) => a + enc.animalCount, 0);
    return { grade, refundCents, dissolving, escaped };
  })();

  const resizePreview = (() => {
    if (resizeEnc === null || (resizeAdd.length === 0 && resizeRemove.length === 0)) return null;
    const trialSegs = [...layout.segments.filter((k) => !resizeRemove.includes(k)), ...resizeAdd.map((a) => a.edge)];
    const trialGates = layout.gates.filter((gt) => !resizeRemove.includes(gt));
    const pens = findPens({ cols, rows, edgeM }, { segments: new Set(trialSegs), gates: new Set(trialGates) }, () => true);
    const anchorPens = pens.filter((pn) => pn.cells.includes(resizeEnc.anchor));
    const tileHa = (edgeM * edgeM) / 10000;
    const addsCents = resizeAdd.reduce((a, p2) => a + edgeM * GRADES[p2.grade - 1].perMCents, 0);
    const refundCents = resizeRemove.reduce((a, k) => {
      const gr = segGrade.get(k) ?? 0;
      return a + (gr >= 1 ? Math.round(edgeM * GRADES[gr - 1].perMCents * 0.4) : 0);
    }, 0);
    const base = { addsCents, refundCents, netCents: addsCents - refundCents };
    if (anchorPens.length !== 1) return { ...base, ok: false as const, reason: 'The keep-side cell is not inside one closed loop - close the new boundary first.' };
    const newPen = anchorPens[0];
    const oldCells = resizeEnc.cells;
    if (newPen.cells.length === oldCells.length && newPen.cells.every((c) => oldCells.includes(c))) {
      return { ...base, ok: false as const, reason: 'Nothing resized - the pen is the same loop.' };
    }
    for (const other of claimedHere) {
      if (other.enc.id === resizeEnc.id) continue;
      if (other.cells.some((c) => newPen.cells.includes(c))) return { ...base, ok: false as const, reason: `This would absorb the ${other.enc.type} pen next door.` };
      const stillClosed = pens.some((pn) => pn.cells.length === other.cells.length && pn.cells.every((c) => other.cells.includes(c)));
      if (!stillClosed) return { ...base, ok: false as const, reason: `This would break the ${other.enc.type} pen next door.` };
    }
    if (resizeEncInfo === undefined) return null;
    const enc = resizeEncInfo.enc;
    const newAreaHa = newPen.areaTiles * tileHa;
    const density = (sim.enclosureTypes[enc.type] as UiEnclosureType | undefined)?.density_per_ha ?? 0;
    const capacity = Math.floor(newAreaHa * density);
    if (newPen.areaTiles < oldCells.length && capacity < enc.animalCount) {
      return { ...base, ok: false as const, reason: `The smaller pen houses ${String(capacity)} but ${String(enc.animalCount)} animal${enc.animalCount === 1 ? '' : 's'} live here - move them out first.` };
    }
    const parcel = sim.parcels.find((pp) => pp.id === parcelId);
    const claimedIds = new Set(claimedHere.map((c) => c.enc.id));
    const usedHa = claimedHere.filter((c) => c.enc.id !== resizeEnc.id).reduce((a, c) => a + c.cells.length * tileHa, 0)
      + sim.enclosures.filter((e2) => e2.parcelId === parcelId && !claimedIds.has(e2.id)).reduce((a, e2) => a + e2.areaHa, 0);
    if (parcel !== undefined && newPen.areaTiles * tileHa > parcel.hectares - usedHa + 1e-9) {
      return { ...base, ok: false as const, reason: 'The resized pen does not fit the parcel area budget.' };
    }
    return { ...base, ok: true as const, areaHa: newAreaHa, perimeterM: newPen.perimeterEdges * edgeM };
  })();

  const line = (key: string, stroke: string, width: number, extra = ''): preact.JSX.Element | null => {
    const ln = edgeLine(key, edgeM);
    if (!ln) return null;
    return <line key={key + extra} x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2} stroke={stroke} stroke-width={width} stroke-linecap="square" />;
  };

  return (
    <div style="margin-top:8px">
      <div class="eyebrow eyebrow-rule">Draw fences - 10 m grid</div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">
        {(['fence', 'gate', 'remove', 'resize', 'pan'] as const).map((t) => (
          <button key={t} data-testid={`tool-${t}`} class={tool === t ? 'btn btn-primary' : 'btn btn-quiet'} style="flex:1;font-size:12px"
            onClick={() => { setTool(t); if (t !== 'resize') { setResizeEnc(null); setResizeAdd([]); setResizeRemove([]); } }}>
            {t === 'fence' ? 'Fence' : t === 'gate' ? 'Gate' : t === 'remove' ? 'Remove' : t === 'resize' ? 'Resize' : 'Pan'}
          </button>
        ))}
        <button class="btn btn-quiet" style="font-size:12px" onClick={() => { setCellPx((p) => Math.max(MIN_CELL_PX, Math.round(p / 1.4))); }} aria-label="Zoom out">-</button>
        <button class="btn btn-quiet" style="font-size:12px" onClick={() => { setCellPx((p) => Math.min(MAX_CELL_PX, Math.round(p * 1.4))); }} aria-label="Zoom in">+</button>
      </div>
      <div style="display:flex;gap:6px;margin-top:6px">
        {GRADES.map((gg) => (
          <button key={gg.grade} class={grade === gg.grade ? 'btn btn-primary' : 'btn btn-quiet'} style="flex:1;font-size:11px"
            onClick={() => { setGrade(gg.grade); }}>
            {gg.name} ${fmtMoney(gg.perMCents)}/m
          </button>
        ))}
      </div>
      <div class="row-label" style="text-transform:none;letter-spacing:0.01em;margin-top:6px;font-size:11px">
        {tool === 'gate'
          ? `Drag to paint gated edges ($${fmtMoney(edgeM * g.perMCents)} each - same as the fence length); tapping an existing fence gates it free.`
          : tool === 'pan'
            ? 'Drag to move around the parcel; +/- to zoom. Switch back to Fence or Gate to paint.'
            : tool === 'resize'
              ? (resizeEnc === null
                ? (claimedHere.length > 0 ? 'Tap a claimed pen to resize.' : 'No claimed pens on this parcel to resize.')
                : `Resizing the ${resizeEncInfo?.enc.type ?? ''} pen - drag to paint NEW edges, tap built edges to mark removal, tap a middle cell to move the keep-side anchor. Instant for now (conversion timing undecided).`)
              : `Drag to paint ${g.name.toLowerCase()} fence - ${edgeM} m x $${fmtMoney(g.perMCents)}/m = $${fmtMoney(edgeM * g.perMCents)} per edge. Nothing is charged until you confirm.`}
      </div>
      <div style="overflow:auto;max-height:340px;margin-top:6px;border:var(--ui-border);border-radius:calc(var(--ui-radius) - 4px);background:var(--ui-parchment-soft)">
        <svg
          ref={svgRef}
          viewBox={`-1 -1 ${W + 2} ${H + 2}`}
          width={cols * cellPx}
          height={rows * cellPx}
          style={`display:block;touch-action:${tool === 'pan' ? 'auto' : 'none'};cursor:${tool === 'pan' ? 'grab' : 'crosshair'};pointer-events:${tool === 'pan' ? 'none' : 'auto'}`}
          data-testid="fence-drawing-grid"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onPointerCancel={endDrag}
        >
          {(layout.claimed ?? []).map((c) => (
            <rect key={`cl-${String(c)}`} x={(c % cols) * edgeM} y={Math.floor(c / cols) * edgeM} width={edgeM} height={edgeM}
              fill={resizeEnc !== null && resizeEnc.cells.includes(c) ? 'rgba(74,90,46,0.38)' : 'rgba(74,90,46,0.18)'} pointer-events="none" />
          ))}
          {resizeEnc !== null && (
            <rect x={(resizeEnc.anchor % cols) * edgeM + edgeM * 0.3} y={Math.floor(resizeEnc.anchor / cols) * edgeM + edgeM * 0.3}
              width={edgeM * 0.4} height={edgeM * 0.4} fill="#b97a2a" pointer-events="none" data-testid="resize-anchor" />
          )}
          {Array.from({ length: cols * rows }, (_, i) => {
            const cx = i % cols; const cy = Math.floor(i / cols);
            return <rect key={i} x={cx * edgeM} y={cy * edgeM} width={edgeM} height={edgeM} fill="none" stroke="#00000014" stroke-width="0.3" />;
          })}
          {layout.segments.map((key) => {
            const gr = GRADES[(segGrade.get(key) ?? 0) - 1] as (typeof GRADES)[number] | undefined;
            const isGate = gateSet.has(key);
            const ln = edgeLine(key, edgeM);
            if (!ln) return null;
            const mid = { x: (ln.x1 + ln.x2) / 2, y: (ln.y1 + ln.y2) / 2 };
            const shrink = isGate ? 0.28 : 0;
            return (
              <g key={key}>
                {line(key, gr?.stroke ?? '#7A6F66', gr?.width ?? 1.4)}
                {isGate && (
                  <line x1={ln.x1 + (mid.x - ln.x1) * shrink} y1={ln.y1 + (mid.y - ln.y1) * shrink}
                    x2={ln.x2 + (mid.x - ln.x2) * shrink} y2={ln.y2 + (mid.y - ln.y2) * shrink}
                    stroke="#b97a2a" stroke-width={(gr?.width ?? 1.4) + 0.6} stroke-linecap="round" />
                )}
              </g>
            );
          })}
          {pending.map((p) => {
            const gr = GRADES[p.grade - 1];
            const ln = edgeLine(p.edge, edgeM);
            if (!ln) return null;
            return (
              <line key={`staged-${p.edge}`} x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2}
                stroke={gr.stroke} stroke-width={gr.width} stroke-dasharray={p.gate ? '2,1.4' : '3,2'} stroke-opacity="0.65" stroke-linecap="square" />
            );
          })}
          {invalidEdge && line(invalidEdge, '#B03A2E', 2.4, '-invalid')}
          {resizeAdd.map((p2) => {
            const gr = GRADES[p2.grade - 1];
            const ln = edgeLine(p2.edge, edgeM);
            if (!ln) return null;
            return (
              <line key={`rz-add-${p2.edge}`} x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2}
                stroke={gr.stroke} stroke-width={gr.width} stroke-dasharray="3,2" stroke-opacity="0.65" stroke-linecap="square" />
            );
          })}
          {resizeRemove.map((k) => line(k, '#B03A2E', 2.8, '-rz-remove'))}
        </svg>
      </div>
      {pending.length > 0 && (
        <div class="card" style="margin-top:6px;padding:8px 12px;border:var(--ui-border)">
          <div class="row-label" style="text-transform:none;letter-spacing:0.01em;font-size:12px" data-testid="fence-cost-preview">
            {pending.length} edge{pending.length === 1 ? '' : 's'} staged - total ${fmtMoney(previewCents)} on confirm
          </div>
          <div style="display:flex;gap:6px;margin-top:6px">
            <button class="btn btn-primary" style="flex:1;padding-left:6px;padding-right:6px;font-size:12px" data-testid="fence-confirm" onClick={confirm}>Confirm ${fmtMoney(previewCents)}</button>
            <button class="btn btn-quiet" style="padding-left:6px;padding-right:6px;font-size:12px" data-testid="fence-undo" onClick={() => { setPending((p) => p.slice(0, -1)); }}>Undo last</button>
            <button class="btn btn-quiet" style="padding-left:6px;padding-right:6px;font-size:12px" data-testid="fence-cancel" onClick={() => { setPending([]); }}>Cancel</button>
          </div>
        </div>
      )}
      {removalInfo !== null && (
        <div class="card" style="margin-top:6px;padding:8px 12px;border:2px solid #A33A2A" data-testid="remove-confirm">
          <div class="row-label" style="text-transform:none;letter-spacing:0.01em;font-size:12px">
            Remove this {removalInfo.grade >= 1 ? GRADES[removalInfo.grade - 1].name.toLowerCase() : ''} fence ({String(edgeM)} m)? Salvage back: ${fmtMoney(removalInfo.refundCents)} (40%).
            {removalInfo.dissolving.map((enc) => (
              <span key={enc.id} style="display:block;margin-top:4px;color:#A33A2A">
                Breaks the {enc.type} loop: enclosure dissolves{enc.animalCount > 0 ? `, ${String(enc.animalCount)} animal${enc.animalCount === 1 ? '' : 's'} inside escape${enc.animalCount === 1 ? 's' : ''} (reputation hit)` : ''}.
              </span>
            ))}
          </div>
          <div style="display:flex;gap:6px;margin-top:6px">
            <button class="btn btn-primary" style="flex:1;background:#A33A2A" data-testid="remove-confirm-btn"
              onClick={() => { bridge.send('fence.remove', { parcelId, edge: pendingRemoval }); setPendingRemoval(null); }}>
              Remove for ${fmtMoney(removalInfo.refundCents)}
            </button>
            <button class="btn btn-quiet" style="flex:1" data-testid="remove-keep-btn" onClick={() => { setPendingRemoval(null); }}>Keep fence</button>
          </div>
        </div>
      )}
      {resizeEnc !== null && (
        <div class="card" style="margin-top:6px;padding:8px 12px;border:var(--ui-border)" data-testid="resize-panel">
          <div class="row-label" style="text-transform:none;letter-spacing:0.01em;font-size:12px">
            Resizing {resizeEncInfo?.enc.type ?? 'pen'} ({String(resizeEncInfo?.enc.animalCount ?? 0)} animal{(resizeEncInfo?.enc.animalCount ?? 0) === 1 ? '' : 's'} inside)
            {resizePreview !== null && (resizePreview.ok
              ? ` -> ${resizePreview.areaHa.toFixed(2)} ha, ${String(resizePreview.perimeterM)} m fence. ${resizeAdd.length} new edge${resizeAdd.length === 1 ? '' : 's'} $${fmtMoney(resizePreview.addsCents)}${resizeRemove.length > 0 ? ` - 40% salvage on ${String(resizeRemove.length)} removed $${fmtMoney(resizePreview.refundCents)} back` : ''}. Net ${resizePreview.netCents >= 0 ? `$${fmtMoney(resizePreview.netCents)}` : `$${fmtMoney(-resizePreview.netCents)} back`}.`
              : ` - ${resizePreview.reason}`)}
            {resizePreview === null && ' Stage new or removed edges to preview. Instant for now (conversion timing undecided).'}
          </div>
          <div style="display:flex;gap:6px;margin-top:6px">
            {resizePreview?.ok === true && (
              <button class="btn btn-primary" style="flex:1;font-size:12px" data-testid="resize-confirm"
                onClick={() => {
                  bridge.send('enclosure.resize', {
                    enclosureId: resizeEnc.id, anchorCell: resizeEnc.anchor,
                    add: resizeAdd.map((a) => ({ edge: a.edge, grade: a.grade, gate: a.gate })),
                    remove: resizeRemove,
                  });
                  setResizeEnc(null); setResizeAdd([]); setResizeRemove([]);
                }}>
                Confirm resize {resizePreview.netCents >= 0 ? `$${fmtMoney(resizePreview.netCents)}` : `($${fmtMoney(-resizePreview.netCents)} back)`}
              </button>
            )}
            <button class="btn btn-quiet" style="flex:1;font-size:12px" data-testid="resize-cancel"
              onClick={() => { setResizeEnc(null); setResizeAdd([]); setResizeRemove([]); }}>
              {resizePreview !== null ? 'Cancel resize' : 'Done'}
            </button>
          </div>
        </div>
      )}
      {unclaimedPens.map((pen) => {
        const t = sim.enclosureTypes[claimType] as UiEnclosureType | undefined;
        const claimCents = (t?.base_cost_cents ?? 0) + (t?.water_point_cost_cents ?? 0);
        return (
          <div class="card" key={`pen-${String(pen.cells[0])}`} style="margin-top:6px;padding:8px 12px;border:var(--ui-border)" data-testid="pen-claim">
            <div class="row-label" style="text-transform:none;letter-spacing:0.01em;font-size:12px">
              Closed pen - {(pen.areaTiles * edgeM * edgeM / 10000).toFixed(2)} ha, {String(pen.perimeterEdges * edgeM)} m fence. Fence already paid; claim adds the enclosure type + water.
            </div>
            <div style="display:flex;gap:6px;margin-top:6px;align-items:center">
              <select class="btn btn-quiet" style="flex:1" value={claimType} onChange={(e) => { setClaimType((e.target as HTMLSelectElement).value); }} data-testid="claim-type">
                {Object.keys(sim.enclosureTypes).map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <button class="btn btn-primary" style="flex:1" data-testid="pen-claim-btn"
                onClick={() => { bridge.send('enclosure.claim', { parcelId, anchorCell: pen.cells[0], type: claimType }); }}>
                Claim as {claimType} ${fmtMoney(claimCents)}
              </button>
            </div>
          </div>
        );
      })}
      <div class="row-label" style="text-transform:none;letter-spacing:0.01em;margin-top:4px;font-size:10px">
        {GRADES.map((gg) => `${gg.name.toLowerCase()} $${fmtMoney(gg.perMCents)}/m`).join(' - ')} - amber stub = gate - scroll to pan, +/- to zoom
      </div>
    </div>
  );
}
